/**
 * Email validation + typo detection — May 4, 2026.
 *
 * Used by the EmailConfirmModal that gates every Tax Cert send /
 * resend action (see DELIVERY-UX requirements). Two layers:
 *
 *   1. validateEmailFormat(email)   → blocking RFC check
 *   2. detectEmailWarnings(...)    → soft, non-blocking warnings
 *      (typo TLDs, common provider typos, client-domain mismatch)
 *
 * Pure functions, no network — runs in the browser per keystroke.
 */

// RFC 5322-ish format check. Strict enough for SES, lenient enough
// for everyday business addresses with subdomains and pluses.
const EMAIL_FORMAT_RE = /^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/;

export function validateEmailFormat(email) {
  if (!email || typeof email !== 'string') return false;
  return EMAIL_FORMAT_RE.test(email.trim());
}

// ---- TLD typo map (typed → corrected) ----------------------------
const TLD_TYPOS = {
  con: 'com',
  cm: 'com',
  cmo: 'com',
  comm: 'com',
  ocm: 'com',
  vom: 'com',
  xom: 'com',
  co: 'com',  // very risky in some markets, but in US-business this is almost always a typo of .com
  nte: 'net',
  ne: 'net',
  ogr: 'org',
  or: 'org',
  rog: 'org',
};

// ---- Common provider domain typos -------------------------------
const PROVIDER_TYPOS = {
  'gmial.com': 'gmail.com',
  'gmaill.com': 'gmail.com',
  'gmai.com': 'gmail.com',
  'gnail.com': 'gmail.com',
  'gmail.cm': 'gmail.com',
  'gmail.con': 'gmail.com',
  'gmail.co': 'gmail.com',
  'yaho.com': 'yahoo.com',
  'yhaoo.com': 'yahoo.com',
  'yahooo.com': 'yahoo.com',
  'yahoo.con': 'yahoo.com',
  'hotnail.com': 'hotmail.com',
  'hotmial.com': 'hotmail.com',
  'hotmal.com': 'hotmail.com',
  'hotmail.con': 'hotmail.com',
  'outlok.com': 'outlook.com',
  'outloook.com': 'outlook.com',
  'outlook.con': 'outlook.com',
  'iclould.com': 'icloud.com',
  'icoud.com': 'icloud.com',
  'icloud.con': 'icloud.com',
  'aol.con': 'aol.com',
  'protonmial.com': 'protonmail.com',
};

function splitEmail(email) {
  const at = email.lastIndexOf('@');
  if (at < 0) return { local: '', domain: '' };
  return { local: email.slice(0, at), domain: email.slice(at + 1).toLowerCase() };
}

function suggestTldFix(domain) {
  const dot = domain.lastIndexOf('.');
  if (dot < 0) return null;
  const root = domain.slice(0, dot);
  const tld = domain.slice(dot + 1);
  if (TLD_TYPOS[tld]) {
    return `${root}.${TLD_TYPOS[tld]}`;
  }
  return null;
}

// Levenshtein distance — capped at 2 for performance.
function levenshtein(a, b) {
  if (a === b) return 0;
  if (Math.abs(a.length - b.length) > 2) return 99;
  const m = a.length, n = b.length;
  const prev = new Array(n + 1).fill(0).map((_, i) => i);
  for (let i = 1; i <= m; i += 1) {
    const curr = [i];
    for (let j = 1; j <= n; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(curr[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
    }
    for (let k = 0; k <= n; k += 1) prev[k] = curr[k];
  }
  return prev[n];
}

/**
 * detectEmailWarnings(email, options)
 *
 *   options.knownDomain    — domain currently configured for this
 *                           client (e.g. "pnatitleservicesllc.com").
 *                           When the typed domain is close-but-not-equal,
 *                           emit a "did-you-mean" warning.
 *   options.knownEmail     — full known-good email (used for messaging).
 *
 * Returns: [{ type, message, suggestion? }, ...]
 *   type ∈ 'tld' | 'provider' | 'client_domain' | 'format'
 */
export function detectEmailWarnings(email, options = {}) {
  const warnings = [];
  if (!email) return warnings;
  const trimmed = email.trim();

  if (!validateEmailFormat(trimmed)) {
    warnings.push({
      type: 'format',
      message: 'This does not look like a valid email address.',
    });
    return warnings;
  }

  const { domain } = splitEmail(trimmed);

  // 1) TLD typo
  const tldFix = suggestTldFix(domain);
  if (tldFix) {
    warnings.push({
      type: 'tld',
      message: `Top-level domain ".${domain.split('.').pop()}" looks like a typo.`,
      suggestion: trimmed.replace(`@${domain}`, `@${tldFix}`),
    });
  }

  // 2) Common provider typo
  if (PROVIDER_TYPOS[domain]) {
    warnings.push({
      type: 'provider',
      message: `"${domain}" looks like a typo of "${PROVIDER_TYPOS[domain]}".`,
      suggestion: trimmed.replace(`@${domain}`, `@${PROVIDER_TYPOS[domain]}`),
    });
  }

  // 3) Client-domain mismatch — when we have a known good domain on
  //    file and the typed domain is 1–2 character edits away from it
  //    (e.g. pnatitleservicesll.com vs pnatitleservicesllc.com),
  //    flag it. Same domain or unrelated domains stay quiet.
  const knownDomain = (options.knownDomain || '').toLowerCase().trim();
  const knownEmail = options.knownEmail || '';
  if (knownDomain && domain && knownDomain !== domain) {
    const dist = levenshtein(domain, knownDomain);
    if (dist >= 1 && dist <= 2) {
      const fix = knownEmail
        ? knownEmail
        : trimmed.replace(`@${domain}`, `@${knownDomain}`);
      warnings.push({
        type: 'client_domain',
        message: `Domain "${domain}" is unusual for this client — did you mean "${knownDomain}"?`,
        suggestion: fix,
      });
    }
  }

  return warnings;
}

/**
 * Convenience: returns true if the email is RFC-valid AND has no
 * warnings. Used by callers who want an at-a-glance "clean" check.
 */
export function isCleanEmail(email, options = {}) {
  if (!validateEmailFormat(email)) return false;
  return detectEmailWarnings(email, options).length === 0;
}

export const __test_only__ = { levenshtein, splitEmail, TLD_TYPOS, PROVIDER_TYPOS };
